import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { MdOutlinePlaylistPlay } from "react-icons/md";
import { useFontStyle } from "~/hook/useFontStyle";
import useIsClient from "~/hook/useIsClient";
import { ImageData } from "~/interface/commonImagesize-types";
import { NodeType } from "~/interface/video-category/type";
import { getYoutubeVideoThumbnail } from "~/lib/helperFuncation/youtubeThumbnails";
import { cn } from "~/lib/utils";
import { BLUR_HASH, COLORS, FALLBACK_IMAGE_URL } from "~/utils/constant";
import { convertSecondsToHMS } from "~/utils/helper-funcations";
import ImageWithSkeleton from "../shared-components/ImageWithSkeleton";
import Tag from "../shared-components/Tag";
import { DISPLAY_TYPE } from "../video-grid/video-data";

export type PropsType = {
  className?: string;
  isChildrenCard?: boolean;
  data: {
    video_id: string | null;
    name: string;
    record_date?: string | null;
    duration_sec?: number | null;
    node_type?: NodeType;
    img: ImageData | null;
    part?: number | null;
    tag?: string | null;
    vertical_img?: ImageData | null;
    square_img: ImageData | null;
    media_type?: string | null;
    playlist_item_count?: number | null;
  };
};

function VideoCard({ className, data }: PropsType) {
  const isPlaylist = data?.node_type ? data?.node_type !== DISPLAY_TYPE.playlist_series : false;
  const { normalInter } = useFontStyle();
  const { t } = useTranslation();
  const isClient = useIsClient();

  return (
    <div className={`w-full flex flex-col gap-1 justify-center items-center cursor-pointer relative group ${className}`}>
      {/* Thumbnail Section */}
      {isPlaylist && (
        <>
          <div
            className="absolute -top-2.5 xs:-top-2 left-1 xs:w-[90%] lg:w-[93%] 2xl:w-[95%] h-[10%] bg-black/30 rounded-xl translate-y-[4px] translate-x-[4px] z-0 opacity-50"
            style={{ backgroundImage: `url(${data?.img?.og?.url || FALLBACK_IMAGE_URL})` }}
          ></div>
          {/* <div className="absolute -top-3.5 xs:-top-2.5 left-4 xs:w-[80%] lg:w-[85%] 2xl:w-[90%] h-[10%] bg-black/20 rounded-xl translate-y-[2px] translate-x-[2px] z-0"></div> */}
        </>
      )}
      {/* Main Thumbnail */}
      <div className="w-full aspect-video relative overflow-hidden z-10 rounded-xl ">
        <ImageWithSkeleton
          url={{
            small: data?.img?.s?.url || (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "low")) || FALLBACK_IMAGE_URL,
            medium: data?.img?.m?.url || (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "medium")) || FALLBACK_IMAGE_URL,
            large: data?.img?.l?.url || (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "medium")) || FALLBACK_IMAGE_URL,
            hd: data?.img?.hd?.url || (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "high")) || FALLBACK_IMAGE_URL,
            original: data?.img?.og?.url || (data?.video_id && getYoutubeVideoThumbnail(data?.video_id, "maximum")) || FALLBACK_IMAGE_URL,
          }}
          alt="thumbnail"
          className="w-full h-full object-cover"
          roundedClass="rounded-xl"
          blur_hash={data?.img?.bh || BLUR_HASH}
          isPreloaded={!isClient}
        />

        {data?.tag && <div className="w-auto absolute top-2 right-2">{<Tag text={data?.tag} />}</div>}

        {/* Playlist Tag */}
        {isPlaylist ? (
          <div className="absolute  bottom-1.5 right-1.5 md:bottom-2 md:right-2 flex items-center  bg-slate-800/70 text-white text-10 md:text-xs px-1 md:px-1 py-0.5 md:py-[3px] rounded-sm z-20 ">
            <MdOutlinePlaylistPlay color={COLORS.white} className="w-4 h-4 md:w-4.5 md:h-4.5" />
            <span className={cn(normalInter)}>
              {data?.part} {t(`Videos`)}
            </span>
          </div>
        ) : data?.duration_sec ? (
          <div className="absolute bottom-1.5 right-1.5 md:bottom-2 md:right-2 flex items-center justify-center bg-slate-800/70 text-white text-10 md:text-xs px-1 md:px-1 py-0.5 md:py-[3px] rounded-sm z-10 xs:min-w-[40px] md:min-w-[40px]">
            <span className={cn(normalInter)}>{convertSecondsToHMS(data?.duration_sec || 0)}</span>
          </div>
        ) : null}
      </div>

      {/* Video Details */}
      <div className="w-full flex flex-col justify-start items-start ">
        <p
          className={cn(
            "common-text-size text-textColorPrimary line-clamp-2 leading-5 overflow-hidden group-hover:text-darkOrangeColor/80 transition-colors duration-200 ease-in-out w-full",
            normalInter,
          )}
          title={data?.name}
        >
          {data?.name || ""}
        </p>

        {(data?.duration_sec || data?.record_date) && (
          <div className="flex justify-start items-center gap-2">
            {data?.record_date && (
              <p className={cn("body_text_xs text-textSecondaryColor", normalInter)}>{dayjs(data?.record_date).format("DD MMM YYYY")}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default VideoCard;
